/**
 * Authentication Helper Utilities
 * Provides common authentication patterns for dashboard pages
 */

import { GuardianService } from '../services/guardian-service';
import { StudentService } from '../services/student-service';
import type { Guardian, Student, GuardianType } from './types';

/**
 * Require guardian authentication with specific guardian type
 * Redirects to auth page if not authenticated or wrong guardian type
 * @param requiredType - The required guardian type (parent, teacher)
 * @returns The authenticated guardian
 */
export async function requireAuth(requiredType: GuardianType): Promise<Guardian> {
  const { guardian, error } = await GuardianService.getCurrentGuardian();

  if (error || !guardian || guardian.guardian_type !== requiredType) {
    window.location.href = '/auth/auth.html?type=' + requiredType;
    throw new Error('Authentication required');
  }

  return guardian;
}

/**
 * Require student authentication
 * Redirects to auth page if not authenticated as student
 * @returns The authenticated student
 */
export async function requireStudentAuth(): Promise<Student> {
  const student = StudentService.getCurrentStudent();

  if (!student) {
    window.location.href = '/auth/auth.html?type=student';
    throw new Error('Student authentication required');
  }

  return student;
}

/**
 * Require guardian authentication with any of the allowed types.
 * Accepts an array of guardian types so shared pages (e.g. child-detail)
 * can be used by both parents and teachers.
 * Redirects to auth page if not authenticated or guardian type not in the allowed list.
 *
 * @param allowedTypes - Array of allowed guardian types, e.g. ['parent', 'teacher']
 * @returns The authenticated guardian
 */
export async function requireGuardianAuth(allowedTypes: GuardianType[]): Promise<Guardian> {
  const { guardian, error } = await GuardianService.getCurrentGuardian();

  if (error || !guardian || !allowedTypes.includes(guardian.guardian_type)) {
    // Redirect using the first allowed type so the auth form pre-selects the right mode
    window.location.href = '/auth/auth.html?type=' + allowedTypes[0];
    throw new Error('Authentication required');
  }

  return guardian;
}

/**
 * Check if guardian is authenticated without redirecting
 * @returns The guardian if authenticated, null otherwise
 */
export async function checkGuardianAuth(): Promise<Guardian | null> {
  const { guardian, error } = await GuardianService.getCurrentGuardian();

  if (error || !guardian) {
    return null;
  }

  return guardian;
}

/**
 * Check if student is authenticated without redirecting
 * @returns The student if authenticated, null otherwise
 */
export function checkStudentAuth(): Student | null {
  return StudentService.getCurrentStudent();
}
